/**
 * Public "Insights" — long-form white-paper content model.
 *
 * This is the DATA layer for the marketing-site Insights section reachable
 * from the public landing page (NOT the authenticated `lib/insights/` tenant
 * correlation-insights module — different concept, deliberately namespaced
 * under `marketing/` to avoid any collision).
 *
 * Articles are plain data so adding a new one is cheap: append an `Article`
 * to `ARTICLES` whose `blocks` are a list from the `ContentBlock` discriminated
 * union below. The infographic + prose kit in
 * `components/insights/marketing/` renders each block via `<BlockRenderer>`.
 *
 * Design rules for authors:
 *   - Body copy is honest + vendor-neutral. The product only appears in the
 *     closing `sahasEnables` block.
 *   - Any numbers in stats / charts / bars are ILLUSTRATIVE and must read as
 *     generic ranges, never as a specific customer's audited result.
 *   - No fabricated customer names or quotes.
 */

// ────────────────────────────────────────────────────────────────────
// Content blocks — the infographic + prose kit, as a discriminated union.
// Each block carries `kind` as its discriminator; `<BlockRenderer>` switches
// on it. Keep this union the single source of truth for what authors can use.
// ────────────────────────────────────────────────────────────────────

/** Section divider + heading. `eyebrow` is the small uppercase kicker above. */
export interface SectionHeadingBlock {
  kind: "sectionHeading";
  eyebrow?: string;
  heading: string;
  /** Optional one-line standfirst under the heading. */
  sub?: string;
}

/**
 * A prose paragraph. `lead: true` renders larger with a decorative drop-cap —
 * use it once, on the opening paragraph of an article.
 */
export interface ParagraphBlock {
  kind: "paragraph";
  text: string;
  lead?: boolean;
}

/** One hero-scale number with a label + supporting caption. */
export interface StatCalloutBlock {
  kind: "statCallout";
  value: string;
  label: string;
  caption?: string;
  /** Visual emphasis tone for the number. Defaults to "brand". */
  tone?: AccentTone;
}

/** A row of 3–4 big numbers (compact metric strip). */
export interface MetricRowBlock {
  kind: "metricRow";
  metrics: Array<{
    value: string;
    label: string;
    caption?: string;
    tone?: AccentTone;
  }>;
}

/**
 * Labelled horizontal bars — ideal for before/after or A-vs-B contrasts.
 * `max` is optional; when omitted the largest value defines full width.
 */
export interface ComparisonBarsBlock {
  kind: "comparisonBars";
  title?: string;
  unit?: string;
  max?: number;
  bars: Array<{
    label: string;
    value: number;
    /** Free-form value annotation shown at the bar end (overrides unit fmt). */
    display?: string;
    tone?: AccentTone;
  }>;
  caption?: string;
}

/** Numbered left-to-right steps with connectors (a process / pipeline). */
export interface ProcessFlowBlock {
  kind: "processFlow";
  title?: string;
  steps: Array<{
    title: string;
    detail?: string;
    /** Optional lucide-react icon name resolved by the renderer. */
    icon?: ProcessIconName;
  }>;
  caption?: string;
}

/** Large pull-quote to break up the column. No attribution to real people. */
export interface PullQuoteBlock {
  kind: "pullQuote";
  quote: string;
  /** Optional generic attribution, e.g. "A plant manager's rule of thumb". */
  attribution?: string;
}

/**
 * A small, deterministic, illustrative chart driven entirely by inline data.
 * No live data — these exist to visualise a point in the prose.
 */
export interface ChartBlock {
  kind: "chart";
  chart: "bar" | "line" | "area";
  title?: string;
  caption?: string;
  /** X-axis category key in each datum. */
  xKey: string;
  /** One or more series plotted against `xKey`. */
  series: Array<{ dataKey: string; label: string; tone?: AccentTone }>;
  data: Array<Record<string, string | number>>;
  /** Optional Y-axis unit suffix for the tooltip / axis. */
  unit?: string;
}

/** Aside / note callout. `tone` colours the accent rail + icon. */
export interface CalloutBlock {
  kind: "callout";
  tone?: AccentTone;
  title?: string;
  text: string;
}

/**
 * Branded closing block — "How {productName} enables this". This is the ONLY
 * place the product is sold. 2–3 capability bullets + a demo CTA.
 */
export interface SahasEnablesBlock {
  kind: "sahasEnables";
  /** Optional intro line; a sensible default is used when omitted. */
  intro?: string;
  capabilities: Array<{ title: string; detail: string; icon?: ProcessIconName }>;
}

/** The full discriminated union an article's `blocks` is built from. */
export type ContentBlock =
  | SectionHeadingBlock
  | ParagraphBlock
  | StatCalloutBlock
  | MetricRowBlock
  | ComparisonBarsBlock
  | ProcessFlowBlock
  | PullQuoteBlock
  | ChartBlock
  | CalloutBlock
  | SahasEnablesBlock;

/** Accent tones map to the design-system semantic palette tokens. */
export type AccentTone = "brand" | "accent" | "success" | "warning" | "danger" | "info";

/**
 * Whitelisted lucide icon names usable from data blocks. The renderer maps
 * these to components so article data never imports React/icon modules.
 */
export type ProcessIconName =
  | "cpu"
  | "gitBranch"
  | "boxes"
  | "lineChart"
  | "gauge"
  | "database"
  | "radio"
  | "shieldCheck"
  | "workflow"
  | "sparkles"
  | "factory"
  | "search"
  | "alertTriangle"
  | "coins"
  | "recycle"
  | "plug";

// ────────────────────────────────────────────────────────────────────
// Article model
// ────────────────────────────────────────────────────────────────────

/** Category label shown as a chip. Drives grouping + colour on the index. */
export type ArticleGoal =
  | "Educational"
  | "ROI"
  | "Quality"
  | "Analytics"
  | "Knowledge"
  | "Technical"
  | "Standards"
  | "Sustainability";

/** Ordering + presentation metadata for goals (index grouping uses this). */
export const ARTICLE_GOALS: Array<{
  id: ArticleGoal;
  label: string;
  blurb: string;
  tone: AccentTone;
}> = [
  {
    id: "Educational",
    label: "Educational",
    blurb: "Plain-language primers on what this technology actually is.",
    tone: "info",
  },
  {
    id: "ROI",
    label: "ROI",
    blurb: "The economics — where the money leaks, and what closing it is worth.",
    tone: "success",
  },
  {
    id: "Quality",
    label: "Quality",
    blurb: "Traceability, defects, and getting more right the first time.",
    tone: "brand",
  },
  {
    id: "Analytics",
    label: "Analytics",
    blurb: "Finding the cause — correlation, SPC, and prediction from data you already have.",
    tone: "warning",
  },
  {
    id: "Knowledge",
    label: "Knowledge",
    blurb: "Capturing the expertise that walks out the door at shift's end.",
    tone: "info",
  },
  {
    id: "Technical",
    label: "Technical",
    blurb: "How it connects to the plant you already run.",
    tone: "accent",
  },
  {
    id: "Standards",
    label: "Standards",
    blurb: "Where this fits the industrial digital-twin standards landscape.",
    tone: "brand",
  },
  {
    id: "Sustainability",
    label: "Sustainability",
    blurb: "Energy, yield, and doing more with less melt.",
    tone: "success",
  },
];

export interface Article {
  /** URL slug — `/insights/:slug`. Stable; don't rename once published. */
  slug: string;
  title: string;
  /** Subtitle / standfirst shown under the title. */
  deck: string;
  /** Category label — one of the ARTICLE_GOALS ids. */
  goal: ArticleGoal;
  /** Estimated reading time in minutes. */
  readingMins: number;
  /** Optional hero stat surfaced in the reader hero + on the index card. */
  heroStat?: { value: string; label: string };
  /** Ordered content blocks rendered top-to-bottom by `<BlockRenderer>`. */
  blocks: ContentBlock[];
}

import { DIGITAL_TWIN_ARTICLE } from "./content/what-is-a-digital-twin";
import { SCRAP_COST_ARTICLE } from "./content/the-real-cost-of-scrap";
import { INDUSTRY_40_ARTICLE } from "./content/industry-40-without-ripping-out-plcs";
import { TRACEABILITY_CHARGE_TO_CASTING_ARTICLE } from "./content/traceability-from-charge-to-casting";
import { ROOT_CAUSE_WITHOUT_DATA_SCIENTIST_ARTICLE } from "./content/root-cause-analysis-without-a-data-scientist";
import { CAPTURING_TRIBAL_KNOWLEDGE_ARTICLE } from "./content/capturing-tribal-knowledge";
import { fromClipboardsToALivePlantModel } from "./content/from-clipboards-to-a-live-plant-model";
import { predictingDefectsSpcAndCorrelations } from "./content/predicting-defects-spc-and-correlations";
import { oeeYieldFirstPassMeasuringWhatMovesTheNeedle } from "./content/oee-yield-first-pass-measuring-what-moves-the-needle";
import { ISO_23247_STANDARDS_ARTICLE } from "./content/iso-23247-digital-twin-standards";
import { FOUNDRY_ARCHETYPES_ARTICLE } from "./content/eight-foundry-archetypes-one-platform";
import { MELT_ENERGY_ARTICLE } from "./content/melt-energy-data-driven-foundries";

/**
 * The article registry. Append new `Article`s here to publish them. Some entries
 * spread + override `goal` to slot the article into a richer goal category than
 * the value baked into its content file.
 */
export const ARTICLES: Article[] = [
  DIGITAL_TWIN_ARTICLE,
  SCRAP_COST_ARTICLE,
  INDUSTRY_40_ARTICLE,
  { ...TRACEABILITY_CHARGE_TO_CASTING_ARTICLE, goal: "Quality" },
  { ...ROOT_CAUSE_WITHOUT_DATA_SCIENTIST_ARTICLE, goal: "Analytics" },
  { ...CAPTURING_TRIBAL_KNOWLEDGE_ARTICLE, goal: "Knowledge" },
  fromClipboardsToALivePlantModel,
  { ...predictingDefectsSpcAndCorrelations, goal: "Analytics" },
  oeeYieldFirstPassMeasuringWhatMovesTheNeedle,
  { ...ISO_23247_STANDARDS_ARTICLE, goal: "Standards" },
  { ...FOUNDRY_ARCHETYPES_ARTICLE, goal: "Technical" },
  { ...MELT_ENERGY_ARTICLE, goal: "Sustainability" },
];

/** Look up a single article by slug. Returns `undefined` for unknown slugs. */
export function getArticle(slug: string): Article | undefined {
  return ARTICLES.find((a) => a.slug === slug);
}

/** Articles sharing a goal, excluding `slug` — used for "More insights". */
export function getRelatedArticles(slug: string, limit = 3): Article[] {
  const current = getArticle(slug);
  const pool = ARTICLES.filter((a) => a.slug !== slug);
  if (!current) return pool.slice(0, limit);
  // Prefer same-goal articles, then fill with the rest.
  const sameGoal = pool.filter((a) => a.goal === current.goal);
  const others = pool.filter((a) => a.goal !== current.goal);
  return [...sameGoal, ...others].slice(0, limit);
}

/** Articles grouped by goal, in `ARTICLE_GOALS` order (for the index). */
export function articlesByGoal(): Array<{ goal: ArticleGoal; articles: Article[] }> {
  return ARTICLE_GOALS.map((g) => ({
    goal: g.id,
    articles: ARTICLES.filter((a) => a.goal === g.id),
  })).filter((group) => group.articles.length > 0);
}
